import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import FormacaoStatsPanel from './components/FormacaoStatsPanel';
import { estadoNormalizado } from '@/utils/estadoUtils';

// ─── Helpers ──────────────────────────────────────────────────────────
function pct(a: number, b: number): number {
  return b === 0 ? 0 : Math.round((a / b) * 100);
}

function formatCurrency(v: number): string {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const AREA_COLORS: Record<string, string> = {
  'Segurança': '#10b981',
  'Administrativa': '#f59e0b',
  'Técnica': '#6366f1',
  'Tecnologia': '#0ea5e9',
  'Ambiental': '#22c55e',
  'Direito': '#f97316',
  'Saúde': '#ef4444',
  'Liderança': '#8b5cf6',
  'Comunicação': '#ec4899',
  'Urbanismo': '#84cc16',
  default: '#94a3b8',
};

function areaColor(cat: string | null): string {
  if (!cat) return AREA_COLORS.default;
  for (const key of Object.keys(AREA_COLORS)) {
    if (cat.toLowerCase().includes(key.toLowerCase())) return AREA_COLORS[key];
  }
  return AREA_COLORS.default;
}

// ─── KPI Card com evolução YoY ────────────────────────────────────────────────
interface KpiCardProps {
  icon: string;
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
  onClick?: () => void;
  yoyDelta?: number | null; // diferença absoluta vs ano anterior
  yoyPct?: number | null;   // percentagem de variação
}

function KpiCard({ icon, label, value, sub, accent = 'bg-zinc-100 text-zinc-600', onClick, yoyDelta, yoyPct }: KpiCardProps) {
  const hasYoy = yoyDelta !== null && yoyDelta !== undefined;
  const isPositive = (yoyDelta ?? 0) >= 0;
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-zinc-100 p-5 flex items-start gap-4 transition-all ${onClick ? 'cursor-pointer hover:border-zinc-200 hover:-translate-y-0.5' : ''}`}
    >
      <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${accent}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[22px] font-bold text-zinc-900 leading-tight tabular-nums">{value}</p>
        <p className="text-[12px] font-semibold text-zinc-500 mt-0.5">{label}</p>
        {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
        {hasYoy && (
          <div className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
            <i className={`${isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[10px]`} />
            {isPositive ? '+' : ''}{yoyDelta} {yoyPct !== null && yoyPct !== undefined ? `(${isPositive ? '+' : ''}${yoyPct}%)` : ''} vs ano anterior
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
export default function FormacaoDashboardPage() {
  const navigate = useNavigate();
  const { formacoes, participantes, documentos, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [mesFiltro, setMesFiltro] = useState<number | null>(null);
  const [anoGrafico, setAnoGrafico] = useState<number | null>(null); // null = "todos"

  // Anos disponíveis
  const anosDisponiveis = useMemo(() => {
    const set = new Set<number>();
    formacoes.forEach((f) => {
      if (f.data_inicio) set.add(new Date(f.data_inicio).getFullYear());
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [formacoes]);

  const anoAtual = new Date().getFullYear();
  const anoAnterior = anoAtual - 1;

  // ── KPIs principais (ano atual) ────────────────────────────────────────────
  const kpisAtual = useMemo(() => {
    const fs = formacoes.filter((f) => f.data_inicio && new Date(f.data_inicio).getFullYear() === anoAtual);
    const concluidas = fs.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const totalHoras = fs.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
    const totalCusto = fs.reduce((s, f) => s + (f.custo ?? 0), 0);
    const orcamento = fs.reduce((s, f) => s + (f.orcamento_previsto ?? 0), 0);
    const idsFs = new Set(fs.map((f) => f.id));
    const partsAno = participantes.filter((p) => idsFs.has(p.formacao_id));
    const participantesUnicos = new Set(partsAno.map((p) => p.funcionario_id)).size;
    const certEmitidos = partsAno.filter((p) => p.certificado_emitido).length;
    return {
      totalAcoes: fs.length,
      concluidas: concluidas.length,
      totalHoras,
      totalCusto,
      orcamento,
      participantesUnicos,
      certEmitidos,
      totalPart: partsAno.length,
    };
  }, [formacoes, participantes, anoAtual]);

  // KPIs ano anterior (para YoY)
  const kpisAnterior = useMemo(() => {
    const fs = formacoes.filter((f) => f.data_inicio && new Date(f.data_inicio).getFullYear() === anoAnterior);
    const idsFs = new Set(fs.map((f) => f.id));
    const partsAno = participantes.filter((p) => idsFs.has(p.formacao_id));
    const participantesUnicos = new Set(partsAno.map((p) => p.funcionario_id)).size;
    return {
      totalAcoes: fs.length,
      totalHoras: fs.reduce((s, f) => s + (f.duracao_horas ?? 0), 0),
      participantesUnicos,
      certEmitidos: partsAno.filter((p) => p.certificado_emitido).length,
    };
  }, [formacoes, participantes, anoAnterior]);

  function yoy(atual: number, anterior: number) {
    const delta = atual - anterior;
    const pctVal = anterior === 0 ? null : Math.round((delta / anterior) * 100);
    return { delta, pct: pctVal };
  }

  // ── KPIs globais (todos os anos) ───────────────────────────────────────────
  const kpisGlobal = useMemo(() => {
    const concluidas = formacoes.filter((f) => estadoNormalizado(f.estado) === 'concluida');
    const totalHoras = formacoes.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
    const totalCusto = formacoes.reduce((s, f) => s + (f.custo ?? 0), 0);
    const orcamento = formacoes.reduce((s, f) => s + (f.orcamento_previsto ?? 0), 0);
    const participantesUnicos = new Set(participantes.map((p) => p.funcionario_id)).size;
    const certEmitidos = participantes.filter((p) => p.certificado_emitido).length;
    const taxaCert = pct(certEmitidos, participantes.length);
    const taxaCobertura = pct(participantesUnicos, funcionarios.length);
    const taxaExecucao = pct(concluidas.length, formacoes.length);
    const semFormacao = funcionarios.length - participantesUnicos;
    return {
      totalAcoes: formacoes.length,
      concluidas: concluidas.length,
      planeadas: formacoes.filter((f) => estadoNormalizado(f.estado) === 'planeada').length,
      totalHoras,
      totalCusto,
      orcamento,
      participantesUnicos,
      totalFuncionarios: funcionarios.length,
      certEmitidos,
      totalPart: participantes.length,
      taxaCert,
      taxaCobertura,
      taxaExecucao,
      semFormacao,
      certificadosDoc: documentos.filter((d) => d.tipo === 'certificado').length,
    };
  }, [formacoes, participantes, documentos, funcionarios]);

  // ── Atividade mensal (multi-ano ou ano selecionado) ────────────────────────
  const atividadeMensal = useMemo(() => {
    if (anoGrafico === null) {
      // Comparação multi-ano: retorna mapa por ano
      const mapaAnos: Record<number, number[]> = {};
      anosDisponiveis.forEach((a) => { mapaAnos[a] = Array(12).fill(0); });
      formacoes.forEach((f) => {
        if (f.data_inicio) {
          const d = new Date(f.data_inicio);
          const a = d.getFullYear();
          if (mapaAnos[a]) mapaAnos[a][d.getMonth()]++;
        }
      });
      return mapaAnos;
    } else {
      const counts: Record<number, number[]> = { [anoGrafico]: Array(12).fill(0) };
      formacoes.forEach((f) => {
        if (f.data_inicio) {
          const d = new Date(f.data_inicio);
          if (d.getFullYear() === anoGrafico) counts[anoGrafico][d.getMonth()]++;
        }
      });
      return counts;
    }
  }, [formacoes, anoGrafico, anosDisponiveis]);

  const anosNoGrafico = Object.keys(atividadeMensal).map(Number).sort((a, b) => a - b);
  const maxMensal = Math.max(...Object.values(atividadeMensal).flat(), 1);

  // Cores por ano no gráfico multi-ano
  const ANO_CORES = ['#18181b', '#10b981', '#f59e0b', '#6366f1', '#ef4444', '#0ea5e9'];

  // ── Formações filtradas pelo mês clicado ───────────────────────────────────
  const formacoesFiltradas = useMemo(() => {
    if (mesFiltro === null) return null;
    return formacoes.filter((f) => {
      if (!f.data_inicio) return false;
      const d = new Date(f.data_inicio);
      const anoOk = anoGrafico === null || d.getFullYear() === anoGrafico;
      return d.getMonth() === mesFiltro && anoOk;
    });
  }, [formacoes, mesFiltro, anoGrafico]);

  // ── Distribuição por área (ano atual) ─────────────────────────────────────
  const porArea = useMemo(() => {
    const map: Record<string, { total: number; concluidas: number; horas: number; participantes: number }> = {};
    formacoes
      .filter((f) => f.data_inicio && new Date(f.data_inicio).getFullYear() === anoAtual)
      .forEach((f) => {
        const cat = f.categoria ?? 'Outros';
        if (!map[cat]) map[cat] = { total: 0, concluidas: 0, horas: 0, participantes: 0 };
        map[cat].total++;
        if (estadoNormalizado(f.estado) === 'concluida') map[cat].concluidas++;
        map[cat].horas += f.duracao_horas ?? 0;
        map[cat].participantes += participantes.filter((p) => p.formacao_id === f.id).length;
      });
    return Object.entries(map)
      .map(([cat, v]) => ({ cat, ...v, pctExec: pct(v.concluidas, v.total) }))
      .sort((a, b) => b.horas - a.horas);
  }, [formacoes, participantes, anoAtual]);

  // ── Últimas formações ─────────────────────────────────────────────────────
  const recentFormacoes = useMemo(() => {
    return [...formacoes]
      .filter((f) => f.data_inicio)
      .sort((a, b) => new Date(b.data_inicio!).getTime() - new Date(a.data_inicio!).getTime())
      .slice(0, 6);
  }, [formacoes]);

  // Top formandos por horas
  const topFormandos = useMemo(() => {
    const horasPorFunc: Record<string, { funcionario_id: string; totalHoras: number; totalAcoes: number; certs: number }> = {};
    participantes.forEach((p) => {
      const formacao = formacoes.find((f) => f.id === p.formacao_id);
      const horas = formacao?.duracao_horas ?? 0;
      if (!horasPorFunc[p.funcionario_id]) {
        horasPorFunc[p.funcionario_id] = { funcionario_id: p.funcionario_id, totalHoras: 0, totalAcoes: 0, certs: 0 };
      }
      horasPorFunc[p.funcionario_id].totalHoras += horas;
      horasPorFunc[p.funcionario_id].totalAcoes++;
      if (p.certificado_emitido) horasPorFunc[p.funcionario_id].certs++;
    });
    return Object.values(horasPorFunc)
      .sort((a, b) => b.totalHoras - a.totalHoras)
      .slice(0, 5)
      .map((item) => {
        const func = funcionarios.find((f) => f.id === item.funcionario_id);
        return { ...item, nome: func?.nome ?? 'Desconhecido', cargo: func?.cargo ?? '' };
      });
  }, [participantes, formacoes, funcionarios]);

  const maxHorasFormando = topFormandos[0]?.totalHoras ?? 1;

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          </div>
          <p className="text-[13px] text-zinc-400">A carregar dashboard...</p>
        </div>
      </main>
    );
  }

  const execOrcPct = kpisGlobal.orcamento > 0 ? pct(kpisGlobal.totalCusto, kpisGlobal.orcamento) : 0;
  const execOrcColor = execOrcPct > 100 ? '#ef4444' : execOrcPct > 80 ? '#f59e0b' : '#10b981';

  const yoyAcoes = yoy(kpisAtual.totalAcoes, kpisAnterior.totalAcoes);
  const yoyHoras = yoy(kpisAtual.totalHoras, kpisAnterior.totalHoras);
  const yoyPart = yoy(kpisAtual.participantesUnicos, kpisAnterior.participantesUnicos);
  const yoyCert = yoy(kpisAtual.certEmitidos, kpisAnterior.certEmitidos);

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-dashboard-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Gestão da Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Resumo Executivo</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Visão consolidada do plano de formação e métricas de execução</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-zinc-400 bg-zinc-100 px-3 py-1.5 rounded-full">
              Exercício {anoAtual}–{anoAtual + 1}
            </span>
            <button
              onClick={() => navigate('/formacao/relatorios')}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-bar-chart-2-line text-sm" /></div>
              Ver Relatórios
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">

        {/* ── KPIs principais (com YoY) ────────────────────────────────────── */}
        <div>
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">
            {anoAtual} — comparação com {anoAnterior}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon="ri-book-open-line"
              label="Ações de Formação"
              value={kpisAtual.totalAcoes}
              sub={`${kpisAtual.concluidas} concluídas`}
              accent="bg-zinc-100 text-zinc-700"
              onClick={() => navigate('/formacao/acoes')}
              yoyDelta={yoyAcoes.delta}
              yoyPct={yoyAcoes.pct}
            />
            <KpiCard
              icon="ri-user-star-line"
              label="Participações"
              value={kpisAtual.totalPart}
              sub={`${kpisAtual.participantesUnicos} funcionários distintos`}
              accent="bg-amber-50 text-amber-600"
              onClick={() => navigate('/formacao/participantes')}
              yoyDelta={yoyPart.delta}
              yoyPct={yoyPart.pct}
            />
            <KpiCard
              icon="ri-medal-line"
              label="Certificados Emitidos"
              value={kpisAtual.certEmitidos}
              sub={`Taxa: ${pct(kpisAtual.certEmitidos, kpisAtual.totalPart)}%`}
              accent="bg-emerald-50 text-emerald-600"
              onClick={() => navigate('/formacao/documentos')}
              yoyDelta={yoyCert.delta}
              yoyPct={yoyCert.pct}
            />
            <KpiCard
              icon="ri-time-line"
              label="Total de Horas"
              value={`${kpisAtual.totalHoras}h`}
              sub={`Média ${kpisAtual.totalAcoes > 0 ? Math.round(kpisAtual.totalHoras / kpisAtual.totalAcoes) : 0}h por ação`}
              accent="bg-zinc-100 text-zinc-600"
              yoyDelta={yoyHoras.delta}
              yoyPct={yoyHoras.pct}
            />
          </div>
        </div>

        {/* KPIs secundários */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon="ri-funds-line"
            label="Custo Total"
            value={kpisGlobal.totalCusto > 0 ? formatCurrency(kpisGlobal.totalCusto) : '—'}
            sub={kpisGlobal.totalHoras > 0 && kpisGlobal.totalCusto > 0 ? `${formatCurrency(Math.round(kpisGlobal.totalCusto / kpisGlobal.totalHoras))}/hora` : 'Sem custos registados'}
            accent="bg-zinc-100 text-zinc-600"
          />
          <KpiCard
            icon="ri-group-line"
            label="Não Formados"
            value={kpisGlobal.semFormacao}
            sub={`${kpisGlobal.taxaCobertura}% de cobertura atingida`}
            accent="bg-amber-50 text-amber-600"
            onClick={() => navigate('/formacao/alertas')}
          />
          <KpiCard
            icon="ri-hourglass-line"
            label="H. por Formando"
            value={kpisGlobal.participantesUnicos > 0 ? `${Math.round(kpisGlobal.totalHoras / kpisGlobal.participantesUnicos)}h` : '—'}
            sub="Média de horas por funcionário"
            accent="bg-emerald-50 text-emerald-600"
          />
          <KpiCard
            icon="ri-building-2-line"
            label="Entidades Formadoras"
            value={new Set(formacoes.map((f) => f.entidade_formadora).filter(Boolean)).size}
            sub="Entidades parceiras ativas"
            accent="bg-zinc-100 text-zinc-600"
            onClick={() => navigate('/formacao/entidades')}
          />
        </div>

        {/* ── Painel de Estatísticas do Ano Atual ─────────────────────────── */}
        <FormacaoStatsPanel ano={anoAtual} />

        {/* ── Métricas + Gráfico mensal ────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Métricas de execução */}
          <div className="bg-white rounded-xl border border-zinc-100 p-5 space-y-4">
            <h3 className="text-[13px] font-bold text-zinc-800">Métricas de Execução</h3>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] font-medium text-zinc-600">Execução do Plano</span>
                <span className="text-[13px] font-bold text-zinc-800">{kpisGlobal.taxaExecucao}%</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${kpisGlobal.taxaExecucao}%`, backgroundColor: '#10b981' }} />
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">{kpisGlobal.concluidas} de {kpisGlobal.totalAcoes} ações concluídas</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] font-medium text-zinc-600">Cobertura de Funcionários</span>
                <span className="text-[13px] font-bold text-zinc-800">{kpisGlobal.taxaCobertura}%</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${kpisGlobal.taxaCobertura}%`, backgroundColor: kpisGlobal.taxaCobertura >= 70 ? '#10b981' : kpisGlobal.taxaCobertura >= 50 ? '#f59e0b' : '#ef4444' }} />
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">{kpisGlobal.participantesUnicos} de {kpisGlobal.totalFuncionarios} funcionários formados</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] font-medium text-zinc-600">Taxa de Certificação</span>
                <span className="text-[13px] font-bold text-zinc-800">{kpisGlobal.taxaCert}%</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${kpisGlobal.taxaCert}%`, backgroundColor: '#10b981' }} />
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">{kpisGlobal.certEmitidos} certificados sobre {kpisGlobal.totalPart} participações</p>
            </div>
            {kpisGlobal.orcamento > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] font-medium text-zinc-600">Execução Orçamental</span>
                  <span className="text-[13px] font-bold text-zinc-800">{execOrcPct}%</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(execOrcPct, 100)}%`, backgroundColor: execOrcColor }} />
                </div>
                <p className="text-[10px] text-zinc-400 mt-1">{formatCurrency(kpisGlobal.totalCusto)} de {formatCurrency(kpisGlobal.orcamento)} previsto</p>
              </div>
            )}
          </div>

          {/* Gráfico mensal clicável com comparação multi-ano */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-100 p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-[13px] font-bold text-zinc-800">
                Formações por Mês
                {mesFiltro !== null && (
                  <span className="ml-2 text-[11px] font-semibold text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full">
                    Filtrado: {MESES[mesFiltro]}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                {/* Seletor de ano */}
                <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 gap-0.5">
                  <button
                    onClick={() => { setAnoGrafico(null); setMesFiltro(null); }}
                    className={`whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${anoGrafico === null ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    Todos
                  </button>
                  {anosDisponiveis.map((a) => (
                    <button
                      key={a}
                      onClick={() => { setAnoGrafico(a); setMesFiltro(null); }}
                      className={`whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${anoGrafico === a ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                {mesFiltro !== null && (
                  <button
                    onClick={() => setMesFiltro(null)}
                    className="whitespace-nowrap text-[10px] font-semibold text-zinc-400 hover:text-zinc-700 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer"
                  >
                    Limpar filtro ×
                  </button>
                )}
              </div>
            </div>

            {/* Legenda multi-ano */}
            {anoGrafico === null && anosNoGrafico.length > 1 && (
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                {anosNoGrafico.map((a, i) => (
                  <div key={a} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: ANO_CORES[i % ANO_CORES.length] }} />
                    <span className="text-[10px] font-semibold text-zinc-500">{a}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Barras */}
            <div className="flex items-end gap-1 h-[100px]">
              {MESES.map((mes, i) => {
                const isSelected = mesFiltro === i;
                return (
                  <div
                    key={i}
                    className="flex-1 flex flex-col items-center gap-0.5 cursor-pointer group"
                    onClick={() => setMesFiltro(mesFiltro === i ? null : i)}
                    title={`${mes} — clica para filtrar`}
                  >
                    <div className="w-full flex items-end justify-center gap-0.5" style={{ height: '88px' }}>
                      {anosNoGrafico.map((a, ai) => {
                        const count = atividadeMensal[a]?.[i] ?? 0;
                        const h = maxMensal === 0 ? 0 : Math.round((count / maxMensal) * 80);
                        const cor = ANO_CORES[ai % ANO_CORES.length];
                        return (
                          <div
                            key={a}
                            className={`rounded-t-sm transition-all ${isSelected ? 'opacity-100' : 'group-hover:opacity-80'}`}
                            style={{
                              height: count === 0 ? '4px' : `${h}px`,
                              backgroundColor: count === 0 ? '#f4f4f5' : cor,
                              width: `${Math.floor(100 / anosNoGrafico.length)}%`,
                              minWidth: '4px',
                              outline: isSelected ? `2px solid ${cor}` : 'none',
                              outlineOffset: '1px',
                            }}
                          />
                        );
                      })}
                    </div>
                    <span className={`text-[9px] font-medium transition-colors ${isSelected ? 'text-zinc-800 font-bold' : 'text-zinc-400'}`}>
                      {mes.slice(0, 1)}
                    </span>
                  </div>
                );
              })}
            </div>

            <p className="text-[10px] text-zinc-400 mt-2">Clica num mês para filtrar as formações abaixo</p>

            {/* Formações filtradas pelo mês */}
            {formacoesFiltradas !== null && (
              <div className="mt-3 pt-3 border-t border-zinc-50">
                <p className="text-[11px] font-bold text-zinc-700 mb-2">
                  {formacoesFiltradas.length} formação(ões) em {MESES[mesFiltro!]}{anoGrafico ? ` ${anoGrafico}` : ''}
                </p>
                {formacoesFiltradas.length === 0 ? (
                  <p className="text-[11px] text-zinc-400">Nenhuma formação neste mês.</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {formacoesFiltradas.map((f) => (
                      <div key={f.id} className="flex items-center gap-2 bg-zinc-50 rounded-lg px-3 py-2">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: areaColor(f.categoria) }} />
                        <p className="text-[11px] font-semibold text-zinc-800 flex-1 truncate">{f.nome}</p>
                        <span className="text-[10px] text-zinc-400 flex-shrink-0">{f.duracao_horas}h</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${estadoNormalizado(f.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : estadoNormalizado(f.estado) === 'em_curso' ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>
                          {estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : estadoNormalizado(f.estado) === 'em_curso' ? 'Em Curso' : 'Planeada'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Horas por Categoria (Balanço Social) ────────────────────────── */}
        <div className="bg-white rounded-xl border border-zinc-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[13px] font-bold text-zinc-800">Horas de Formação por Categoria — {anoAtual}</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">Balanço social — distribuição de horas por área temática</p>
            </div>
            <button
              onClick={() => navigate('/formacao/relatorios')}
              className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
            >
              Ver relatório →
            </button>
          </div>
          {porArea.length === 0 ? (
            <p className="text-[12px] text-zinc-400 py-4 text-center">Sem dados para {anoAtual}</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
              {porArea.map((area) => {
                const maxHoras = Math.max(...porArea.map((a) => a.horas), 1);
                const barW = Math.round((area.horas / maxHoras) * 100);
                const cor = areaColor(area.cat);
                return (
                  <div key={area.cat} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cor }} />
                    <p className="text-[12px] font-medium text-zinc-700 w-36 flex-shrink-0 truncate">{area.cat}</p>
                    <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${barW}%`, backgroundColor: cor }} />
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-right">
                      <span className="text-[12px] font-bold text-zinc-800 tabular-nums w-12">{area.horas}h</span>
                      <span className="text-[10px] text-zinc-400 w-16">{area.participantes} part.</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-10 text-center ${area.pctExec >= 80 ? 'bg-emerald-50 text-emerald-600' : area.pctExec >= 40 ? 'bg-amber-50 text-amber-600' : 'bg-zinc-100 text-zinc-500'}`}>
                        {area.pctExec}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* Totais */}
          {porArea.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-50 flex items-center gap-6 flex-wrap">
              <div>
                <p className="text-[18px] font-bold text-zinc-900">{porArea.reduce((s, a) => s + a.horas, 0)}h</p>
                <p className="text-[10px] text-zinc-400">Total de horas em {anoAtual}</p>
              </div>
              <div>
                <p className="text-[18px] font-bold text-zinc-900">{porArea.reduce((s, a) => s + a.participantes, 0)}</p>
                <p className="text-[10px] text-zinc-400">Total de participações</p>
              </div>
              <div>
                <p className="text-[18px] font-bold text-zinc-900">{porArea.length}</p>
                <p className="text-[10px] text-zinc-400">Áreas temáticas</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Estado das Ações ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-zinc-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[13px] font-bold text-zinc-800">Estado das Ações</h3>
            <button onClick={() => navigate('/formacao/acoes')} className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">Ver todas →</button>
          </div>
          <div className="space-y-3">
            {[
              { estado: 'concluida', label: 'Concluídas', color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700' },
              { estado: 'em_curso', label: 'Em Curso', color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700' },
              { estado: 'planeada', label: 'Planeadas', color: '#71717a', bg: 'bg-zinc-100', text: 'text-zinc-600' },
              { estado: 'cancelada', label: 'Canceladas', color: '#ef4444', bg: 'bg-red-50', text: 'text-red-600' },
            ].map((item) => {
              const count = formacoes.filter((f) => estadoNormalizado(f.estado) === item.estado).length;
              const pctVal = kpisGlobal.totalAcoes > 0 ? Math.round((count / kpisGlobal.totalAcoes) * 100) : 0;
              return (
                <div key={item.estado} className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${item.bg}`}>
                    <span className={`text-[14px] font-bold ${item.text}`}>{count}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] font-medium text-zinc-700">{item.label}</span>
                      <span className="text-[11px] text-zinc-400">{pctVal}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pctVal}%`, backgroundColor: item.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Ações recentes ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-50 flex items-center justify-between">
            <h3 className="text-[13px] font-bold text-zinc-800">Ações Recentes</h3>
            <button onClick={() => navigate('/formacao/acoes')} className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">Ver todas →</button>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-50 bg-zinc-50/50">
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Ação</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide hidden lg:table-cell">Entidade</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Data</th>
                <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Part.</th>
                <th className="px-5 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
              </tr>
            </thead>
            <tbody>
              {recentFormacoes.map((f, i) => {
                const isConcluida = estadoNormalizado(f.estado) === 'concluida';
                return (
                  <tr key={f.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors cursor-pointer ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`} onClick={() => navigate('/formacao/acoes')}>
                    <td className="px-5 py-3">
                      <p className="text-[12px] font-semibold text-zinc-800 max-w-[240px] truncate">{f.nome}</p>
                      {f.categoria && <span className="text-[10px] text-zinc-400">{f.categoria}</span>}
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell">
                      <p className="text-[12px] text-zinc-500 max-w-[180px] truncate">{f.entidade_formadora ?? '—'}</p>
                    </td>
                    <td className="px-5 py-3">
                      {f.data_inicio ? (
                        <p className="text-[12px] text-zinc-600">{new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      ) : <span className="text-[12px] text-zinc-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className="text-[13px] font-bold text-zinc-700">{f.total_participantes ?? 0}</span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${isConcluida ? 'bg-emerald-50 text-emerald-700' : estadoNormalizado(f.estado) === 'planeada' ? 'bg-zinc-100 text-zinc-500' : estadoNormalizado(f.estado) === 'em_curso' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isConcluida ? 'bg-emerald-500' : estadoNormalizado(f.estado) === 'planeada' ? 'bg-zinc-400' : estadoNormalizado(f.estado) === 'em_curso' ? 'bg-amber-500' : 'bg-rose-400'}`} />
                        {isConcluida ? 'Concluída' : estadoNormalizado(f.estado) === 'planeada' ? 'Planeada' : estadoNormalizado(f.estado) === 'em_curso' ? 'Em Curso' : 'Cancelada'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Top Formandos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-zinc-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[13px] font-bold text-zinc-800">Top 5 — Mais Horas de Formação</h3>
              <button onClick={() => navigate('/formacao/funcionarios')} className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">Ver funcionários →</button>
            </div>
            {topFormandos.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-full">
                  <i className="ri-user-search-line text-zinc-400 text-sm" />
                </div>
                <p className="text-[12px] text-zinc-400">Sem dados de participações</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topFormandos.map((f, i) => (
                  <div key={f.funcionario_id} className="flex items-center gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 text-[10px] font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-500'}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[12px] font-semibold text-zinc-800 truncate">{f.nome}</p>
                        <span className="text-[12px] font-bold text-zinc-800 tabular-nums flex-shrink-0">{f.totalHoras}h</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.round((f.totalHoras / maxHorasFormando) * 100)}%`, backgroundColor: i === 0 ? '#f59e0b' : '#10b981' }} />
                        </div>
                        <span className="text-[10px] text-zinc-400 flex-shrink-0">{f.totalAcoes} ação{f.totalAcoes !== 1 ? 'ões' : ''}</span>
                        {f.certs > 0 && <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex-shrink-0">{f.certs} cert.</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Atalhos rápidos */}
          <div className="bg-white rounded-xl border border-zinc-100 p-5">
            <h3 className="text-[13px] font-bold text-zinc-800 mb-4">Acesso Rápido</h3>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { icon: 'ri-book-open-line', label: 'Ações de Formação', to: '/formacao/acoes', accent: 'bg-zinc-900 text-white' },
                { icon: 'ri-user-star-line', label: 'Participantes', to: '/formacao/participantes', accent: 'bg-zinc-100 text-zinc-600' },
                { icon: 'ri-clipboard-line', label: 'Plano de Formação', to: '/formacao/plano', accent: 'bg-zinc-100 text-zinc-600' },
                { icon: 'ri-folder-open-line', label: 'Documentos', to: '/formacao/documentos', accent: 'bg-emerald-50 text-emerald-600' },
                { icon: 'ri-notification-3-line', label: 'Alertas', to: '/formacao/alertas', accent: 'bg-amber-50 text-amber-600' },
                { icon: 'ri-scales-3-line', label: 'Requisitos Legais', to: '/formacao/requisitos', accent: 'bg-zinc-100 text-zinc-600' },
              ].map((item) => (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  className="whitespace-nowrap bg-zinc-50 rounded-xl p-3 flex items-center gap-2.5 hover:bg-zinc-100 transition-all cursor-pointer text-left"
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${item.accent}`}>
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <span className="text-[11px] font-semibold text-zinc-700">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

      </div>
    </main>
  );
}
